import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Tabs,
    Card,
    Typography,
    Button,
    Spin,
    App,
    Row,
    Col,
    Steps,
    Space, Badge, Input, Modal, Table
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import {authService, cashRegisterService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import HorizontalViewItem from "../../components/HorizontalItems/ViewItem";
import Link from "antd/es/typography/Link";
import CashTrackingSessionModal from "../../components/CashTrackingSessionModal";

// Функция для форматирования даты
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} в ${hours}:${minutes}`;
};

const {Title, Text} = Typography;

// Компонент для отображения времени продажи алкоголя
const AlcoholSaleTimeDisplay = ({ value }) => {
    // Конвертация секунд во время
    const secondsToTime = (seconds) => {
        if (seconds === undefined || seconds === null) return '00:00';
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    if (!value || !Array.isArray(value) || value.length === 0) {
        return <Text>Не указано</Text>;
    }

    return (
        <div>
            {value.map((timeSlot, index) => (
                <div key={index} style={{ marginBottom: 8 }}>
                    <Text>{secondsToTime(timeSlot.from)} - {secondsToTime(timeSlot.to)}</Text>
                </div>
            ))}
        </div>
    );
};

const CashRegisterPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cashRegister, setCashRegister] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [logoutModalVisible, setLogoutModalVisible] = useState(false);
    const [authModalVisible, setAuthModalVisible] = useState(false);
    const [authCode, setAuthCode] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);
    const [formData, setFormData] = useState({
        fiscal_cashes: []
    });
    const [trackingSessions, setTrackingSessions] = useState([]);
    const [trackingSessionsLoading, setTrackingSessionsLoading] = useState(false);
    const [trackingSessionsLoaded, setTrackingSessionsLoaded] = useState(false);
    const [trackingSessionsNextCursor, setTrackingSessionsNextCursor] = useState(null);
    const [trackingSessionsHasMore, setTrackingSessionsHasMore] = useState(true);
    const [trackingSessionsTotal, setTrackingSessionsTotal] = useState(0);
    const [trackingSessionsError, setTrackingSessionsError] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [sessionModalVisible, setSessionModalVisible] = useState(false);

    const trackingSessionsObserver = useRef();
    const lastTrackingSessionElementRef = useCallback(node => {
        if (trackingSessionsLoading) return;
        if (trackingSessionsObserver.current) trackingSessionsObserver.current.disconnect();
        trackingSessionsObserver.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && trackingSessionsHasMore) {
                loadMoreTrackingSessions();
            }
        });
        if (node) trackingSessionsObserver.current.observe(node);
    }, [trackingSessionsLoading, trackingSessionsHasMore, trackingSessionsError]);

    useEffect(() => {
        fetchData();
        setPageTitle('Просмотр кассы');

        setRightContent(null);

        return () => {
            setPageTitle('');
            setRightContent(null);
            setCashRegister(null);
        };
    }, [setPageTitle, setRightContent, id, navigate]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Получаем данные кассы
            const cashRegisterResponse = await cashRegisterService.getCashRegisterById(id);

            // Получаем данные для формы
            const formDataResponse = await cashRegisterService.getFormData();

            if (cashRegisterResponse.status === 'success' && formDataResponse.status === 'success') {
                setCashRegister(cashRegisterResponse.data);
                setFormData(formDataResponse.data);
                console.log('Получены данные кассы:', cashRegisterResponse.data);
            } else {
                message.error('Не удалось загрузить данные кассы');
                navigate('/cash-registers');
            }

            updateRightContent(cashRegisterResponse.data);
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Касса не найдена",
                    description: "Указанная касса не существует или была удалена."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных кассы. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            setHistoryLoading(true);
            const response = await cashRegisterService.getCashRegisterHistory(id);

            if (response.status === 'success') {
                setHistoryData(response.data);
            } else {
                message.error('Не удалось загрузить историю изменений');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при загрузке истории изменений');
        } finally {
            setHistoryLoading(false);
            setHistoryLoaded(true);
        }
    };

    const fetchTrackingSessions = async () => {
        try {
            setTrackingSessionsLoading(true);
            setTrackingSessions([]);
            setTrackingSessionsNextCursor(null);
            setTrackingSessionsHasMore(true);
            setTrackingSessionsError(false);

            const response = await cashRegisterService.getCashTrackingSessions(id);

            if (response && response.status === 'success') {
                setTrackingSessions(response.data.items);
                setTrackingSessionsNextCursor(response.data.pagination.next_cursor);
                setTrackingSessionsHasMore(response.data.pagination.has_more);
                setTrackingSessionsTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить сессии трекинга');
            }
        } catch (error) {
            setTrackingSessionsError(true);
            message.error(error.message || 'Ошибка при загрузке сессий трекинга');
        } finally {
            setTrackingSessionsLoading(false);
            setTrackingSessionsLoaded(true);
        }
    };

    const loadMoreTrackingSessions = async () => {
        if (!trackingSessionsHasMore || trackingSessionsLoading || trackingSessionsError) return;

        try {
            setTrackingSessionsLoading(true);

            const response = await cashRegisterService.getCashTrackingSessions(id, {
                cursor: trackingSessionsNextCursor
            });

            if (response && response.status === 'success') {
                setTrackingSessions(prev => [...prev, ...response.data.items]);
                setTrackingSessionsNextCursor(response.data.pagination.next_cursor);
                setTrackingSessionsHasMore(response.data.pagination.has_more);
                setTrackingSessionsTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить больше сессий трекинга');
            }
        } catch (error) {
            setTrackingSessionsError(true);
            message.error(error.message || 'Ошибка при загрузке дополнительных сессий трекинга');
        } finally {
            setTrackingSessionsLoading(false);
        }
    };

    const handleSessionClick = async (sessionId) => {
        try {
            const response = await cashRegisterService.getCashTrackingSessionById(sessionId);
            if (response.status === 'success') {
                setSelectedSession(response.data);
                setSessionModalVisible(true);
            } else {
                message.error('Не удалось загрузить данные сессии');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при загрузке данных сессии');
        }
    };

    // Функция для получения лейбла из списка опций
    const getOptionLabel = (value, options) => {
        if (!value || !options) return 'Не указано';
        const option = options.find(opt => opt.value === value || opt.id === value);
        return option ? (option.label || option.name) : value;
    };

    const handleLogout = async () => {
        try {
            setLogoutLoading(true);
            const response = await cashRegisterService.logoutAllDevices(
                id,
                uuidv4(),
                uuidv4()
            );

            if (response.status === 'success') {
                message.success('Устройства успешно разлогинены');
                setLogoutModalVisible(false);
                const updatedData = { ...cashRegister, device_auth: false };
                setCashRegister(updatedData);
                updateRightContent(updatedData);
            } else {
                message.error(response.message || 'Ошибка при разлогинивании');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при разлогинивании');
        } finally {
            setLogoutLoading(false);
        }
    };

    const handleAuth = async () => {
        if (!authCode || authCode.length !== 4) {
            message.error('Введите 4-значный код авторизации');
            return;
        }

        try {
            setAuthLoading(true);
            const response = await cashRegisterService.authDevice(
                id,
                authCode,
                uuidv4(),
                uuidv4()
            );

            if (response.status === 'success') {
                message.success('Устройство успешно авторизовано');
                setAuthModalVisible(false);
                setAuthCode('');
                const updatedData = { ...cashRegister, device_auth: true };
                setCashRegister(updatedData);
                updateRightContent(updatedData);

            } else {
                message.error(response.message || 'Ошибка при авторизации');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при авторизации');
        } finally {
            setAuthLoading(false);
        }
    };

    const updateRightContent = (cashRegisterData) => {
        setRightContent(
            <Space>
                {cashRegisterData?.device_auth === true ? (
                    <Button
                        type="default"
                        danger
                        onClick={() => setLogoutModalVisible(true)}
                    >
                        Разлогинить
                    </Button>
                ) : (
                    <Button
                        type="default"
                        onClick={() => setAuthModalVisible(true)}
                    >
                        Авторизовать
                    </Button>
                )}
                {authService.hasPermission('pos:cash_registers:edit') && (
                    <Button
                        type="primary"
                        onClick={() => navigate(`/cash-registers/${id}/edit`)}
                    >
                        Редактировать
                    </Button>
                )}
            </Space>
        );
    };

    // Функция для форматирования списка способов оплаты
    const formatPaymentMethods = (paymentMethods) => {
        if (!paymentMethods || !Array.isArray(paymentMethods) || paymentMethods.length === 0) {
            return 'Не указано';
        }
        return paymentMethods.map(method => method.name).join(', ');
    };

    // Функция для форматирования налоговой ставки
    const formatTaxRate = (taxRate) => {
        if (!taxRate) return 'Без НДС';
        return `${taxRate}%`;
    };

    // Создаем данные для таблицы сессий трекинга
    const trackingSessionsTableData = trackingSessions.map((session, index) => ({
        ...session,
        key: session.id
    }));

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    return (
        <Card>
            <Title level={3} style={{marginTop: 0}}>
                Касса «{cashRegister.name}»
            </Title>
            <Space>
                <Badge
                    status={cashRegister.device_auth === true ? 'success' : 'error'}
                    text={cashRegister.device_auth === true ? 'Используется' : 'Неактивна'}
                />
                <Text>ID: <Text>{cashRegister.id}</Text></Text>
            </Space>

            <Tabs defaultActiveKey="data" items={[
                {
                    key: "data",
                    label: "Данные",
                    children: (
                        <Row gutter={24}>
                            <Col span={12}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Основные атрибуты</Title>
                                    <HorizontalViewItem label="Название">
                                        <Text>{cashRegister.name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Внешний ID">
                                        <Text>{cashRegister.external_id || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="НДС">
                                        <Text>{formatTaxRate(cashRegister.tax_rate)}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Ширина чека (мм)">
                                        <Text>{cashRegister.receipt_width ? `${cashRegister.receipt_width} мм` : 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Виртуальная касса">
                                        <Text>{cashRegister.is_virtual === 'yes' ? 'Да' : 'Нет'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Принтер чеков">
                                        <Text>{cashRegister.receipt_printer_name || 'Не указано'}</Text>
                                    </HorizontalViewItem>
                                </Space>

                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Способы оплаты</Title>
                                    <HorizontalViewItem label="Доступные способы">
                                        <Text>{formatPaymentMethods(cashRegister.payment_methods)}</Text>
                                    </HorizontalViewItem>
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Фискальные данные</Title>
                                    <HorizontalViewItem label="Фискальная касса">
                                        {cashRegister.fiscal_cash ? (
                                            <Link href={`/fiscal-cash-registers/${cashRegister.fiscal_cash.id}`}>
                                                {cashRegister.fiscal_cash.name}
                                            </Link>
                                        ) : (
                                            <Text>Не указано</Text>
                                        )}
                                    </HorizontalViewItem>
                                </Space>

                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Время продажи алкоголя</Title>
                                    <HorizontalViewItem label="Разрешенное время">
                                        <AlcoholSaleTimeDisplay value={cashRegister.alcohol_sale_time} />
                                    </HorizontalViewItem>
                                </Space>

                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Системная информация</Title>
                                    <HorizontalViewItem label="Дата создания">
                                        <Text>{formatDate(cashRegister.created_at)}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Дата обновления">
                                        <Text>{formatDate(cashRegister.updated_at)}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Процент кэшбека бонусной системы">
                                        <Text>{cashRegister.customer_bonus_percent || 'Не указано'}</Text>
                                    </HorizontalViewItem>
                                </Space>
                            </Col>
                        </Row>
                    )
                },
                {
                    key: "history",
                    label: "История изменений",
                    children: (
                        <Card>
                            {historyLoading ? (
                                <div style={{textAlign: 'center', padding: '50px 0'}}>
                                    <Spin size="large"/>
                                </div>
                            ) : (
                                <Steps
                                    direction="vertical"
                                    size="small"
                                    current={historyData.length}
                                    progressDot
                                    items={historyData.map(item => ({
                                        title: item.change_description,
                                        description: `${item.full_name} (${item.username}), ${formatDate(item.created_at)}`
                                    }))}
                                />
                            )}
                        </Card>
                    )
                },
                {
                    key: "tracking-sessions",
                    label: "Сессии трекинга",
                    children: (
                        <Card>
                            {trackingSessionsLoading && trackingSessions.length === 0 ? (
                                <div style={{textAlign: 'center', padding: '50px 0'}}>
                                    <Spin size="large"/>
                                </div>
                            ) : (
                                <>
                                    <style>
                                        {`
                                            .table-horizontal-borders .ant-table-tbody > tr > td {
                                                border-left: none !important;
                                                border-right: none !important;
                                            }
                                            .table-horizontal-borders .ant-table-thead > tr > th {
                                                border-left: none !important;
                                                border-right: none !important;
                                            }
                                            .table-horizontal-borders .ant-table-tbody > tr:hover > td {
                                                background-color: #f5f5f5 !important;
                                            }
                                        `}
                                    </style>
                                    <Table
                                        columns={[
                                            {
                                                title: 'ID',
                                                dataIndex: 'id',
                                                key: 'id',
                                                width: 80,
                                            },
                                            {
                                                title: 'Открыта',
                                                dataIndex: 'opened_at',
                                                key: 'opened_at',
                                                render: (text) => formatDate(text),
                                            },
                                            {
                                                title: 'Закрыта',
                                                dataIndex: 'closed_at',
                                                key: 'closed_at',
                                                render: (text) => text ? formatDate(text) : '',
                                            },
                                            {
                                                title: 'Статус',
                                                dataIndex: 'status',
                                                key: 'status',
                                                render: (status) => (
                                                    <Badge
                                                        status={status === 'open' ? 'processing' : 'success'}
                                                        text={status === 'open' ? 'Открыта' : 'Закрыта'}
                                                    />
                                                ),
                                            },
                                            {
                                                title: 'Расхождение',
                                                dataIndex: 'discrepancy',
                                                key: 'discrepancy',
                                                render: (discrepancy) => discrepancy ? `${parseFloat(discrepancy).toLocaleString()} ₸` : '—',
                                            }
                                        ]}
                                        dataSource={trackingSessionsTableData}
                                        pagination={false}
                                        size="middle"
                                        bordered={false}
                                        className="table-horizontal-borders"
                                        tableLayout="fixed"
                                        scroll={{x: 800}}
                                        onRow={(record) => ({
                                            onClick: () => handleSessionClick(record.id),
                                            style: { cursor: 'pointer' }
                                        })}
                                    />
                                    {trackingSessionsLoading && (
                                        <div style={{textAlign: 'center', padding: '20px 0'}}>
                                            <Spin/>
                                        </div>
                                    )}
                                    {/* Элемент для отслеживания прокрутки */}
                                    {trackingSessionsHasMore && (
                                        <div
                                            ref={lastTrackingSessionElementRef}
                                            style={{height: 10, width: '100%'}}
                                        />
                                    )}
                                    {!trackingSessionsHasMore && trackingSessions.length > 0 && (
                                        <div style={{textAlign: 'center', padding: '10px'}}>
                                            <Typography.Text type="secondary">
                                                Больше записей нет
                                            </Typography.Text>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card>
                    )
                }
            ]}
                  onChange={(key) => {
                      if (key === 'history' && !historyLoaded) {
                          fetchHistory();
                      } else if (key === 'tracking-sessions' && !trackingSessionsLoaded) {
                          fetchTrackingSessions();
                      }
                  }}/>
            <Modal
                title="Разлогинить устройства"
                open={logoutModalVisible}
                onOk={handleLogout}
                onCancel={() => setLogoutModalVisible(false)}
                okText="Разлогинить"
                cancelText="Отмена"
                confirmLoading={logoutLoading}
                okButtonProps={{ danger: true }}
            >
                <p>Вы уверены, что хотите разлогинить все устройства для данной кассы?</p>
                <p>Касса на текущем устройстве будет разлогинена.</p>
            </Modal>

            <Modal
                title="Авторизация устройства"
                open={authModalVisible}
                onOk={handleAuth}
                onCancel={() => {
                    setAuthModalVisible(false);
                    setAuthCode('');
                }}
                okText="Авторизовать"
                cancelText="Отмена"
                confirmLoading={authLoading}
            >
                <p>Введите 4-значный код авторизации:</p>
                <Input
                    placeholder="0000"
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    maxLength={4}
                    style={{ width: '100px', textAlign: 'center', fontSize: '18px' }}
                />
            </Modal>
            <CashTrackingSessionModal
                visible={sessionModalVisible}
                onClose={() => {
                    setSessionModalVisible(false);
                    setSelectedSession(null);
                }}
                sessionData={selectedSession}
            />
        </Card>
    );
}

export default CashRegisterPage;